import {
  type RailMenuEntry,
  type RailMenuItem,
  type RailRow,
  type RailRowActionId,
  railRowMenu,
} from '@modules/common/components/rail-menu/rail-menu';
import { type VirtualFolder } from '@modules/calendar-app/types/virtual-folder';

/** Whose Calendar rail row a menu belongs to: the member's My Workspace or the Team Workspace. */
export type CalendarRailScope = 'mine' | 'team';

/** A Calendar rail row's key: the app's virtual folders plus the Shared header, which is not a folder. */
export type CalendarRailKey = VirtualFolder | 'shared';

/** Calendar's rail rows by the prototype's names. Rows not listed here keep their own menu. */
export const CALENDAR_RAIL_ROWS: Readonly<Partial<Record<CalendarRailKey, RailRow>>> = {
  all_events: 'all',
  all_calendars: 'all',
  assigned_to_me: 'assigned',
  shared: 'shared',
  flagged: 'flagged',
  followed: 'followup',
  snoozed: 'snoozed',
  following: 'following',
  archived: 'archive',
  deleted: 'trash',
};

/**
 * Rows whose menu does not switch the calendar area when it opens (`sidebar-context-menu.component.ts` `ngOnInit`):
 * the event form would offer the other workspace's calendar there, so "New event" is Coming soon on them.
 */
const NO_AREA_SWITCH: ReadonlySet<CalendarRailKey> = new Set<CalendarRailKey>(['all_calendars', 'deleted', 'archived']);

const TEAM_CLEAR_TITLES = {
  flagged: 'Unflag all',
  followed: 'Remove all follow-ups',
  snoozed: 'Unsnooze all',
} as const;

const TEAM_CLEAR_HINTS = {
  flagged: 'This removes flags for everyone in the team.',
  followed: 'Event follow-up reminders are shared: this clears them for everyone in the team.',
  snoozed: 'Event snoozes are shared: this clears them for everyone in the team.',
} as const;

/**
 * "App settings…" on a Calendar rail row (teamcalendar r1): a Team Workspace row opens Team apps › Calendar, a My
 * Workspace row My apps › Calendar. Team logins only; a login with no team has no such entry.
 */
export const CALENDAR_APP_SETTINGS_ENTRY = 'app-settings';

export const PINS_SHARED_HINT = 'Pins in this workspace are shared by the team.';

/**
 * A Calendar workspace rail row's menu: the prototype's entries from the shared definition, plus Calendar's own
 * entries kept beside them (additive rule). `undefined` for a row the shared definition does not cover.
 *
 * Every destructive bulk entry is Coming soon except the My Workspace "Empty trash", which keeps today's confirmed
 * purge. Team flag / follow-up / snooze clears are Coming soon: on events they are one state shared by the whole
 * team and no frontend-only bound can match what the row lists. The only live Team clear is Following.
 */
export function calendarRowMenu(
  key: CalendarRailKey,
  scope: CalendarRailScope,
  options: { readonly appSettings?: boolean } = {},
): readonly RailMenuItem[] | undefined {
  const row = CALENDAR_RAIL_ROWS[key];
  if (!row) return undefined;
  const team = scope === 'team';

  const live = new Set<RailRowActionId>(['new-container', 'settings']);
  if (!NO_AREA_SWITCH.has(key)) live.add('new-item');
  if (team) {
    if (key === 'following') live.add('clear');
  } else if (key === 'flagged' || key === 'followed' || key === 'snoozed') {
    live.add('clear');
  }

  const extras: RailMenuEntry[] = [];
  if (team && (key === 'flagged' || key === 'followed' || key === 'snoozed')) {
    extras.push({
      id: 'clear-team',
      title: TEAM_CLEAR_TITLES[key],
      icon: 'sapphire-close',
      hint: TEAM_CLEAR_HINTS[key],
      comingSoon: true,
    });
  }
  if (key === 'archived') {
    extras.push({ id: 'delete-all', title: 'Delete all', icon: 'sapphire-trash', dangerous: true, comingSoon: true });
  }
  if (key === 'deleted') {
    extras.push({
      id: 'empty-trash-all',
      title: 'Empty trash',
      icon: 'sapphire-trash',
      dangerous: true,
      comingSoon: team,
    });
  }

  if (options.appSettings) {
    // Calendar's own entry (additive rule): the settings of the workspace this row belongs to, a team login's only.
    extras.push({
      id: CALENDAR_APP_SETTINGS_ENTRY,
      title: 'App settings…',
      icon: 'sapphire-settings',
      hint: team ? 'Team apps › Calendar' : 'My apps › Calendar',
    });
  }

  return railRowMenu({
    row,
    itemNoun: 'event',
    containerNoun: 'calendar',
    readable: false,
    live,
    extras,
  });
}

/**
 * The menu of the Pinned/Team Knots or Tags row. The prototype's own entry manages the member's personal
 * pins, which a team's shared pins cannot be (Coming soon); Calendar's Add and Manage stay beside it.
 */
export function calendarPinnedMenu(kind: 'knots' | 'tags', scope: CalendarRailScope): readonly RailMenuItem[] {
  const team = scope === 'team';
  return [
    { id: 'manage-pins', title: `Manage my pinned ${kind}`, icon: 'sapphire-settings', comingSoon: team },
    null,
    { id: 'add-meta', title: `Add ${kind}`, icon: 'sapphire-plus' },
    {
      id: 'manage-meta',
      title: `Manage ${kind}`,
      icon: 'sapphire-settings',
      hint: team ? PINS_SHARED_HINT : undefined,
    },
  ];
}
